import z from 'zod'
import {
  mintAudienceWebRtcTurnRestCredential,
  parseAudienceWebRtcTurnUrlsJson
} from '../helpers/audienceWebRtcTurnRest'

/**
 * Subset of `RTCIceServer` suitable for JSON over the wire.
 * Configure TURN (and optional extra STUN) via worker env `AUDIENCE_WEBRTC_ICE_SERVERS` JSON array.
 */
export const audienceWebRtcIceServerSchema = z.object({
  urls: z.union([z.string().min(1), z.array(z.string().min(1)).min(1)]),
  username: z.string().optional(),
  credential: z.string().optional()
}).strict()

export const audienceWebRtcIceServersSchema = z.array(audienceWebRtcIceServerSchema)

export type TAudienceWebRtcIceServer = z.infer<typeof audienceWebRtcIceServerSchema>

/** Default public STUN; always included unless env provides a full custom list (see `resolveAudienceWebRtcIceServers`). */
export const DEFAULT_AUDIENCE_WEBRTC_STUN_SERVERS: TAudienceWebRtcIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' }
]

export function parseAudienceWebRtcIceServersJson(raw: string | undefined | null): TAudienceWebRtcIceServer[] | null {
  if (raw == null || raw.trim() === '') {
    return null
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    const r = audienceWebRtcIceServersSchema.safeParse(parsed)
    return r.success ? r.data : null
  }
  catch {
    return null
  }
}

/**
 * Resolves ICE servers for players and the management app.
 * When `AUDIENCE_WEBRTC_ICE_SERVERS` is valid JSON array, entries are **appended** after default public STUN.
 * When missing or invalid, returns default STUN only.
 */
export function resolveAudienceWebRtcIceServers(envJson: string | undefined): TAudienceWebRtcIceServer[] {
  const extra = parseAudienceWebRtcIceServersJson(envJson)
  if (extra != null && extra.length > 0) {
    return [...DEFAULT_AUDIENCE_WEBRTC_STUN_SERVERS, ...extra]
  }
  return [...DEFAULT_AUDIENCE_WEBRTC_STUN_SERVERS]
}

/** Parsed Worker env slice for async ICE resolution (static JSON + optional coturn REST minting). */
export interface IAudienceWebRtcIceEnvAsync {
  AUDIENCE_WEBRTC_ICE_SERVERS?: string
  /** Shared secret aligned with coturn `static-auth-secret` / `use-auth-secret`. */
  AUDIENCE_WEBRTC_TURN_REST_SECRET?: string
  /** Single-line JSON array of `turn:` / `turns:` URL strings (no credentials). */
  AUDIENCE_WEBRTC_TURN_URLS?: string
  /** Optional TTL for minted credentials (seconds). Clamped to 300..604800; default 86400. */
  AUDIENCE_WEBRTC_TURN_CREDENTIAL_TTL_SECONDS?: string
  /** Optional coturn REST username suffix (after `expiry:`). Default `lucimark`. */
  AUDIENCE_WEBRTC_TURN_USERNAME_SUFFIX?: string
}

/**
 * Resolves ICE servers for players and the management app (Workers-safe async path).
 *
 * Order: default public STUN → optional **minted** TURN (when `AUDIENCE_WEBRTC_TURN_REST_SECRET` +
 * valid `AUDIENCE_WEBRTC_TURN_URLS`) → optional static entries from `AUDIENCE_WEBRTC_ICE_SERVERS`.
 */
export async function resolveAudienceWebRtcIceServersAsync(
  env: IAudienceWebRtcIceEnvAsync
): Promise<TAudienceWebRtcIceServer[]> {
  const staticExtra = parseAudienceWebRtcIceServersJson(env.AUDIENCE_WEBRTC_ICE_SERVERS)
  const secretTrimmed = (env.AUDIENCE_WEBRTC_TURN_REST_SECRET ?? '').trim()
  const turnUrls = parseAudienceWebRtcTurnUrlsJson(env.AUDIENCE_WEBRTC_TURN_URLS)
  const out: TAudienceWebRtcIceServer[] = [...DEFAULT_AUDIENCE_WEBRTC_STUN_SERVERS]

  if (secretTrimmed !== '' && turnUrls != null && turnUrls.length > 0) {
    const ttlRaw = env.AUDIENCE_WEBRTC_TURN_CREDENTIAL_TTL_SECONDS
    let ttl = ttlRaw != null && ttlRaw.trim() !== '' ? Number.parseInt(ttlRaw, 10) : 86_400
    if (!Number.isFinite(ttl)) {
      ttl = 86_400
    }
    ttl = Math.min(Math.max(ttl, 300), 604_800)
    const expiryUnix = Math.floor(Date.now() / 1000) + ttl
    const { username, credential } = await mintAudienceWebRtcTurnRestCredential({
      sharedSecret: secretTrimmed,
      expiryUnix,
      usernameSuffix: env.AUDIENCE_WEBRTC_TURN_USERNAME_SUFFIX
    })
    out.push({
      urls: turnUrls.length === 1 ? turnUrls[0]! : turnUrls,
      username,
      credential
    })
  }

  if (staticExtra != null && staticExtra.length > 0) {
    out.push(...staticExtra)
  }

  return out
}
